/**
 * Moyens de paiement LOCAUX d'une page de don (Bancontact d'une association
 * belge — chantier « autres pays », étape B, 21/10/2026). Partagé par les
 * pages /pay, /donate, /c/…/payment et /campaigns/donate.
 *
 * - `moyensLocaux` : publiés par l'API (`payment_providers.stripe.
 *   moyens_locaux`) : ceux que l'association a allumés (France : Wero
 *   seulement, éteint par défaut — 01/11/2026) ; vide en widget intégré (la
 *   banque redirige hors de l'iframe) et pour un don mensuel.
 * - `payerParMoyenLocal` : mêmes contrôles du formulaire que le bouton
 *   « Faire un don », puis départ vers la banque.
 * - `erreurMoyenLocal` : refus ou formulaire incomplet, affiché sous le bouton
 *   du moyen — jamais sur la carte, qui ne sert pas à ce paiement.
 * - `erreurTitulaireMoyenLocal` / `erreurEmailMoyenLocal` : refus de la banque
 *   qui vise un champ (nom du titulaire trop court, e-mail) — affiché SUR ce
 *   champ, traduit. Jamais le texte anglais de Stripe (Karim 01/11/2026 :
 *   « Invalid parameter owner[name]… » en rouge sous le bouton).
 */
import { computed, ref, toValue, watch, type ComputedRef, type MaybeRefOrGetter, type Ref } from "vue";
import { estMoyenPaiementLocal, moyenLocalMensuel } from "@donoor/utils";
import type { MajMoyensPaiement } from "~/composables/payment/usePaymentSocket";
import type { CheckoutError, MoyenPaiementLocalPage } from "@donoor/checkout";
import type { OrgPaymentProviders } from "~/composables/payment/useOrganizationPaymentInfo";
import { extractApiError } from "~/utils/apiError";
import { logger } from "~/utils/logger";

export interface UseMoyensLocauxDonOptions {
  paymentProviders: Ref<OrgPaymentProviders | null | undefined> | ComputedRef<OrgPaymentProviders | null | undefined>;
  /** Vrai : aucun moyen local (widget intégré). */
  masquer: ComputedRef<boolean>;
  /**
   * Don mensuel choisi : seuls les moyens prévus pour (prélèvement SEPA,
   * 02/11/2026) restent proposés ; les autres servent aux dons ponctuels.
   */
  donMensuel?: ComputedRef<boolean>;
  /** Formulaire de prélèvement SEPA de Stripe (IBAN + mandat), monté dans le panneau du moyen. */
  prelevementSepa?: {
    monter: (conteneur: string, mensuel: boolean) => Promise<boolean>;
    demonter: () => void;
  };
  /** Contrôle le formulaire (montant, e-mail, nom) ; faux = ne pas payer. */
  formulaireValide: () => boolean;
  payer: (moyen: MoyenPaiementLocalPage, nomTitulaire: string) => Promise<{ error?: CheckoutError }>;
  isLoading: Ref<boolean>;
  messageGenerique: () => string;
  /** Formulaire incomplet : message sous le bouton du moyen (jamais sous la carte). */
  messageFormulaireInvalide: () => string;
  /** Formulaire valide mais sans nom (champ facultatif laissé vide) : le moyen l'exige. */
  messageNomManquant: (moyen: MoyenPaiementLocalPage) => string;
  /** La banque refuse le nom du titulaire (trop court…) : message du champ « nom ». */
  messageNomInvalide: (moyen: MoyenPaiementLocalPage) => string;
  /** La banque refuse l'e-mail : message du champ « e-mail ». */
  messageEmailInvalide: () => string;
  /**
   * En direct (Karim 01/11/2026) : l'association allume ou éteint un moyen
   * dans ses réglages, le bouton apparaît ou disparaît sur la page ouverte.
   * La connexion temps réel de la page (déjà ouverte pour la confirmation du
   * paiement) rejoint la salle de l'association dès qu'elle est connectée —
   * et de nouveau après une reconnexion.
   */
  enDirect?: {
    socket: {
      isConnected: Ref<boolean>;
      suivreMoyensPaiement: (organizationId: string, onMaj: (maj: MajMoyensPaiement) => void) => void;
    };
    organizationId: MaybeRefOrGetter<string | number | null | undefined>;
  };
  /**
   * Link allumé ou éteint EN DIRECT par l'association (02/11/2026). Le bouton
   * Link est retiré ou remis par Stripe (réglage du compte) : la page n'a qu'à
   * remonter son paiement express pour que Stripe renvoie le nouvel état.
   */
  surChangementLink?: () => void | Promise<void>;
}

/** Le refus vise-t-il le nom du titulaire ? (Stripe : `owner[name]`, `billing_details[name]`) */
export function refusSurLeNom(e: CheckoutError): boolean {
  return /owner\[name\]|billing_details\]?\[name\]|^name$/i.test(e.param ?? "") || /owner\[name\]|billing_details\]\[name\]/i.test(e.message ?? "");
}

/** Le refus vise-t-il l'e-mail ? */
export function refusSurLEmail(e: CheckoutError): boolean {
  return /email/i.test(e.param ?? "") || /billing_details\]\[email\]/i.test(e.message ?? "");
}

export function useMoyensLocauxDon(opts: UseMoyensLocauxDonOptions) {
  /** Dernier état reçu en direct ; null : celui chargé avec la page. */
  const enDirect = ref<{ moyens: MoyenPaiementLocalPage[]; identifiant: string | null } | null>(null);
  const moyensLocaux = computed<MoyenPaiementLocalPage[]>(() => {
    if (opts.masquer.value) return [];
    const publies = enDirect.value?.moyens ?? opts.paymentProviders.value?.stripe?.moyens_locaux ?? [];
    return opts.donMensuel?.value ? publies.filter((m) => estMoyenPaiementLocal(m) && moyenLocalMensuel(m)) : publies;
  });
  /** Numéro officiel de l'association, quand un moyen local l'exige (iDEAL). */
  const identifiantOfficiel = computed<string | null>(() => {
    if (moyensLocaux.value.length === 0) return null;
    return enDirect.value ? enDirect.value.identifiant : (opts.paymentProviders.value?.stripe?.identifiant_officiel ?? null);
  });

  /** Dernier état de Link reçu en direct (null : celui chargé avec la page). */
  let linkConnu: boolean | null = null;
  if (opts.enDirect) {
    const { socket, organizationId } = opts.enDirect;
    watch(
      [() => toValue(organizationId), socket.isConnected],
      ([id, connecte]) => {
        if (!connecte || id == null || id === "") return;
        socket.suivreMoyensPaiement(String(id), (maj) => {
          // Seuls les moyens connus du site (un nom inattendu ne casse jamais la page).
          const moyens = (Array.isArray(maj.moyens_locaux) ? maj.moyens_locaux : []).filter(estMoyenPaiementLocal);
          enDirect.value = { moyens, identifiant: maj.identifiant_officiel ?? null };
          // Link : seulement quand il CHANGE (l'état est aussi envoyé à
          // l'entrée dans la salle — remonter le bouton à chaque fois le ferait
          // clignoter pour rien).
          if (typeof maj.link === "boolean") {
            const avant = linkConnu ?? (opts.paymentProviders.value?.stripe?.link !== false);
            linkConnu = maj.link;
            if (maj.link !== avant) void Promise.resolve(opts.surChangementLink?.()).catch((err) => logger.warn("[useMoyensLocauxDon] bouton express non remonté", err));
          }
        });
      },
      { immediate: true },
    );
  }
  const erreurMoyenLocal = ref<string | null>(null);
  const erreurTitulaireMoyenLocal = ref<string | null>(null);
  const erreurEmailMoyenLocal = ref<string | null>(null);

  async function payerParMoyenLocal(choix: { moyen: MoyenPaiementLocalPage; nomTitulaire: string }): Promise<void> {
    erreurMoyenLocal.value = null;
    erreurTitulaireMoyenLocal.value = null;
    erreurEmailMoyenLocal.value = null;
    if (!opts.formulaireValide()) {
      erreurMoyenLocal.value = opts.messageFormulaireInvalide();
      return;
    }
    if (!choix.nomTitulaire.trim()) {
      erreurMoyenLocal.value = opts.messageNomManquant(choix.moyen);
      return;
    }
    opts.isLoading.value = true;
    try {
      const resultat = await opts.payer(choix.moyen, choix.nomTitulaire);
      if (resultat.error) {
        // Le texte de Stripe est en anglais et technique : seulement au journal.
        logger.warn(`Refus ${choix.moyen} :`, resultat.error.code, resultat.error.param, resultat.error.message);
        if (refusSurLeNom(resultat.error)) erreurTitulaireMoyenLocal.value = opts.messageNomInvalide(choix.moyen);
        else if (refusSurLEmail(resultat.error)) erreurEmailMoyenLocal.value = opts.messageEmailInvalide();
        else erreurMoyenLocal.value = opts.messageGenerique();
        opts.isLoading.value = false;
      }
      // Sans erreur, le navigateur part vers la banque.
    } catch (erreur: unknown) {
      logger.error("Erreur paiement par moyen local:", erreur);
      erreurMoyenLocal.value = extractApiError(erreur).message;
      opts.isLoading.value = false;
    }
  }

  /** Pour le composant : monte / démonte le formulaire SEPA (ponctuel ou mensuel selon la page). */
  const prelevement = opts.prelevementSepa
    ? {
        monter: (conteneur: string) => opts.prelevementSepa!.monter(conteneur, !!opts.donMensuel?.value),
        demonter: () => opts.prelevementSepa!.demonter(),
      }
    : null;

  return {
    moyensLocaux,
    identifiantOfficiel,
    erreurMoyenLocal,
    erreurTitulaireMoyenLocal,
    erreurEmailMoyenLocal,
    payerParMoyenLocal,
    prelevement,
  };
}
